import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PERFIL_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Administrador',
  GARCOM: 'Garçom',
  COZINHA: 'Cozinha',
};

export default function AdminLayout() {
  const { user, empresa, perfilRestaurante, empresas, selecionarEmpresa, logout } = useAuth();
  const navigate = useNavigate();
  // Papel dentro do restaurante (perfil do vínculo; ADMIN global = SUPER_ADMIN)
  const isAdmin = perfilRestaurante === 'SUPER_ADMIN';
  const ehCozinha = perfilRestaurante === 'COZINHA';
  const [menuAberto, setMenuAberto] = useState(false);
  const [trocaAberta, setTrocaAberta] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /** Troca de restaurante: novo token com tenant e recarrega o painel. */
  const handleTrocar = async (tenantId: string) => {
    setTrocando(true);
    try {
      await selecionarEmpresa(tenantId);
      window.location.href = '/restaurante';
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao trocar de restaurante');
      setTrocando(false);
    }
  };

  const links = [
    ...(isAdmin
      ? [
          { to: '/restaurante', label: '📊 Dashboard' },
          { to: '/restaurante/products', label: '🍽️ Produtos' },
          { to: '/restaurante/categories', label: '🏷️ Categorias' },
          { to: '/restaurante/mesas', label: '🪑 Mesas' },
        ]
      : []),
    { to: '/restaurante/orders', label: '📋 Pedidos' },
    ...(!ehCozinha ? [{ to: '/restaurante/comandas', label: '🧾 Comandas' }] : []),
    ...(isAdmin ? [{ to: '/restaurante/financeiro', label: '💰 Financeiro' }] : []),
    ...(isAdmin ? [{ to: '/restaurante/estoque', label: '📦 Estoque' }] : []),
    ...(isAdmin ? [{ to: '/restaurante/users', label: '👥 Usuários' }] : []),
  ];

  // Item ativo: compara a rota atual (Dashboard = raiz /restaurante, exata; demais = prefixo)
  const itemAtivo = (to: string) =>
    to === '/restaurante' ? location.pathname === '/restaurante' : location.pathname.startsWith(to);

  const classeItem = (to: string) =>
    itemAtivo(to)
      ? 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-primary-600/15 text-primary-400 border-l-[3px] border-primary-500'
      : 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white';

  const selosRestaurante = (
    <div className="px-4 pt-3 pb-1 border-t border-gray-800 shrink-0">
      <p className="text-sm text-gray-400">
        Logado como: <span className="text-gray-200">{user?.name}</span>
      </p>
      {empresa && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {empresa.logo ? (
            <img
              src={empresa.logo}
              alt={empresa.nome}
              className="w-6 h-6 rounded-md object-cover border border-gray-700"
            />
          ) : (
            <span>🏪</span>
          )}
          <span className="text-xs font-medium text-gray-300">{empresa.nome}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-800 text-gray-400">
            {PERFIL_LABEL[empresa.perfil] ?? empresa.perfil}
          </span>
          {empresa.visitante && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/50 text-amber-300">
              visitante (operador)
            </span>
          )}
        </div>
      )}
      {empresas.length > 1 && (
        <div className="relative mt-2">
          <button
            onClick={() => setTrocaAberta(!trocaAberta)}
            disabled={trocando}
            className="btn-secondary text-xs w-full"
          >
            {trocando ? 'Trocando...' : '🔄 Trocar de restaurante'}
          </button>
          {trocaAberta && (
            <div className="absolute bottom-full left-0 right-0 mb-1 card p-1 z-50 max-h-48 overflow-y-auto">
              {empresas.map((e) => (
                <button
                  key={e.id}
                  onClick={() => handleTrocar(e.id)}
                  className={`block w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-800 ${
                    e.id === empresa?.id ? 'text-primary-400' : 'text-gray-200'
                  }`}
                >
                  {e.nome}{' '}
                  <span className="text-gray-500 text-xs">({PERFIL_LABEL[e.perfil] ?? e.perfil})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <button onClick={handleLogout} className="btn-secondary text-sm w-full mt-2">
        Sair
      </button>
    </div>
  );

  const menuConteudo = (
    <>
      <div className="flex items-center justify-between px-4 h-14 border-b border-gray-800 shrink-0">
        <Link to="/restaurante" className="font-bold text-primary-400">
          PrimeClick
        </Link>
        <button
          onClick={() => setMenuAberto(false)}
          className="md:hidden text-gray-400 hover:text-gray-200 text-xl leading-none"
          aria-label="Fechar menu"
        >
          ✕
        </button>
      </div>
      <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={classeItem(link.to)}
            onClick={() => setMenuAberto(false)}
          >
            {link.label}
          </Link>
        ))}
      </nav>
      {selosRestaurante}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-950 md:grid md:grid-cols-[16rem_1fr]">
      {/* Sidebar fixa (desktop) */}
      <aside className="hidden md:flex flex-col bg-gray-900 border-r border-gray-800 sticky top-0 h-screen">
        {menuConteudo}
      </aside>

      {/* Gaveta (celular) */}
      {menuAberto && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMenuAberto(false)}
            aria-label="Fechar menu"
          />
          <div className="relative h-full w-72 bg-gray-900 flex flex-col shadow-xl">
            {menuConteudo}
          </div>
        </div>
      )}

      <div className="min-w-0">
        {/* Barra superior (celular) */}
        <div className="md:hidden flex items-center justify-between h-14 px-3 bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuAberto(true)}
              className="text-gray-300 hover:text-white text-2xl leading-none px-1"
              aria-label="Abrir menu"
            >
              ☰
            </button>
            <span className="font-bold text-primary-400">PrimeClick</span>
          </div>
          <button onClick={handleLogout} className="btn-secondary text-sm">
            Sair
          </button>
        </div>

        <main className="px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
